// Uppföljningen i kontot (ADR 0072): inställningarna och uppföljningarna per
// status. Designen saknar skärmen, så den följer kontots övriga sidor.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiFel, data } from '../api/client.js';
import type { components } from '../api/schema.js';
import { useAuth, useMe } from '../auth/auth.js';
import { Sida } from '../layout/Sida.js';
import {
  arStatus,
  dagOchTid,
  FLIKAR,
  kund,
  statusChip,
  statusText,
  type Status,
} from '../uppfoljning/texter.js';

type Installningar = components['schemas']['FollowUpSettings'];

export function Uppfoljning() {
  const { api } = useAuth();
  const [params] = useSearchParams();
  const status: Status = arStatus(params.get('status'))
    ? (params.get('status') as Status)
    : 'awaiting_approval';

  const lista = useQuery({
    queryKey: ['uppfoljningar', status],
    queryFn: async () =>
      data(await api.GET('/v1/admin/follow-ups', { params: { query: { status } } })),
  });

  return (
    <Sida titel="Uppföljning">
      <p className="ingress katalog-huvud">
        När kunden inte har svarat på offerten skriver AI ett kort mejl i er ton. Målet är att
        kunden bokar ett uppstartssamtal.
      </p>
      <Installningskort />
      <nav className="filter" aria-label="Status">
        {FLIKAR.map((f) => (
          <Link
            key={f.kod}
            to={f.kod === 'awaiting_approval' ? '?' : `?status=${f.kod}`}
            className={`filterknapp${status === f.kod ? ' vald' : ''}`}
            aria-current={status === f.kod ? 'page' : undefined}
          >
            {f.namn}
            {lista.data && <span className="antal">{lista.data.counts[f.kod]}</span>}
          </Link>
        ))}
      </nav>
      {lista.isError ? (
        <div className="meddelande" role="alert">
          Uppföljningarna gick inte att hämta. Ladda om sidan och försök igen.
        </div>
      ) : !lista.data ? (
        <p role="status">Hämtar uppföljningarna …</p>
      ) : lista.data.items.length === 0 ? (
        <div className="kort tom">
          <p>
            {status === 'awaiting_approval'
              ? 'Inga utkast väntar på er. Ni får ett mejl när ett väntar.'
              : 'Inget här just nu.'}
          </p>
        </div>
      ) : (
        <ul className="uppfoljningar" aria-label={FLIKAR.find((f) => f.kod === status)?.namn}>
          {lista.data.items.map((u) => (
            <li key={u.id} className="kort">
              <div className="uppfoljning-huvud">
                <Link className="radlank" to={`/uppfoljning/${u.id}`}>
                  {u.subject ?? `Uppföljning av offert ${u.quote?.number ?? ''}`}
                </Link>
                <span className={`chip ${statusChip(u.status)}`}>{statusText(u.status)}</span>
              </div>
              <div className="hjalp">
                {kund(u)}
                {u.quote && (
                  <>
                    {' · '}
                    <Link to={`/forfragningar/${u.quote.inquiryId}`}>Offert {u.quote.number}</Link>
                  </>
                )}
                {' · '}
                {u.status === 'sent' && u.sentAt
                  ? `Skickad ${dagOchTid(u.sentAt)}`
                  : u.status === 'cancelled' && u.cancelledAt
                    ? `Stoppad ${dagOchTid(u.cancelledAt)}`
                    : `Dags ${dagOchTid(u.dueAt)}`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Sida>
  );
}

function Installningskort() {
  const me = useMe();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const kanAndra = me.user.role === 'owner';
  const id = useId();

  const installningar = useQuery({
    queryKey: ['uppfoljning-installningar'],
    queryFn: async () => data(await api.GET('/v1/admin/follow-up-settings')),
  });

  const [form, setForm] = useState<Installningar | null>(null);
  const [egenAvsandare, setEgenAvsandare] = useState(false);
  const [namn, setNamn] = useState('');
  const [epost, setEpost] = useState('');
  const [kvitto, setKvitto] = useState(false);

  useEffect(() => {
    const d = installningar.data;
    if (!d || form) return;
    setForm(d);
    setEgenAvsandare(d.sender !== null);
    setNamn(d.sender?.name ?? '');
    setEpost(d.sender?.email ?? '');
  }, [installningar.data, form]);

  const spara = useMutation({
    mutationFn: async (kropp: Installningar) =>
      data(await api.PUT('/v1/admin/follow-up-settings', { body: kropp })),
    onSuccess: (svar) => {
      queryClient.setQueryData(['uppfoljning-installningar'], svar);
      setKvitto(true);
    },
  });

  if (!form) {
    return installningar.isError ? (
      <div className="meddelande" role="alert">
        Inställningarna gick inte att hämta.
      </div>
    ) : null;
  }

  const epostOk = !egenAvsandare || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(epost.trim());
  const namnOk = !egenAvsandare || namn.trim().length > 0;
  const andra = (andring: Partial<Installningar>) => {
    setKvitto(false);
    setForm({ ...form, ...andring });
  };

  function skicka(e: FormEvent) {
    e.preventDefault();
    if (!form || !epostOk || !namnOk) return;
    spara.mutate({
      ...form,
      sender: egenAvsandare ? { name: namn.trim(), email: epost.trim() } : null,
    });
  }

  return (
    <form className="uppfoljning-installningar" onSubmit={skicka} noValidate>
      <fieldset className="kort flikkort panel-falt" disabled={!kanAndra || spara.isPending}>
        <legend className="avsnitt-rubrik legend-rubrik">Inställningar</legend>
        {!kanAndra && (
          <p className="hjalp" role="note">
            Bara byråns ägare kan ändra inställningarna.
          </p>
        )}
        <label className="vaxel">
          <span>
            <span className="vaxel-namn">Följ upp offerter automatiskt</span>
            <span className="hjalp">Av tills ni slår på det.</span>
          </span>
          <input
            type="checkbox"
            role="switch"
            checked={form.enabled}
            onChange={(e) => andra({ enabled: e.target.checked })}
          />
        </label>
        <div className="tva">
          <div className="falt">
            <label className="etikett" htmlFor={`${id}-dagar`}>
              Dagar efter att offerten skickades
            </label>
            <input
              id={`${id}-dagar`}
              className="in mono tal"
              type="number"
              min={1}
              max={30}
              value={form.delayDays}
              onChange={(e) =>
                andra({
                  delayDays: Math.min(30, Math.max(1, Math.trunc(Number(e.target.value)) || 1)),
                })
              }
            />
            <div className="hjalp">Mejlet går kl. 09 en arbetsdag.</div>
          </div>
          <fieldset className="falt">
            <legend className="etikett">Utskick</legend>
            <div className="val-lista">
              {(
                [
                  ['approve_first', 'Godkänn först', 'Ni får ett mejl och godkänner utkastet här.'],
                  ['auto', 'Automatiskt', 'Utkastet skickas direkt när det är dags.'],
                ] as const
              ).map(([kod, titel, text]) => (
                <label key={kod} className={`val${form.mode === kod ? ' valt' : ''}`}>
                  <input
                    type="radio"
                    name={`${id}-lage`}
                    checked={form.mode === kod}
                    onChange={() => andra({ mode: kod })}
                  />
                  <span>
                    <span className="val-namn">{titel}</span>
                    <span className="val-text">{text}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        <label className="vaxel">
          <span>
            <span className="vaxel-namn">Egen avsändare</span>
            <span className="hjalp">
              Annars er kontaktperson från Utskick. Kundens svar går hit.
            </span>
          </span>
          <input
            type="checkbox"
            role="switch"
            checked={egenAvsandare}
            onChange={(e) => {
              setKvitto(false);
              setEgenAvsandare(e.target.checked);
            }}
          />
        </label>
        {egenAvsandare && (
          <div className="tva">
            <div className="falt">
              <label className="etikett" htmlFor={`${id}-namn`}>
                Namn
              </label>
              <input
                id={`${id}-namn`}
                className="in"
                value={namn}
                aria-invalid={namnOk ? undefined : true}
                onChange={(e) => {
                  setKvitto(false);
                  setNamn(e.target.value);
                }}
              />
            </div>
            <div className="falt">
              <label className="etikett" htmlFor={`${id}-epost`}>
                E-post
              </label>
              <input
                id={`${id}-epost`}
                className="in"
                type="email"
                value={epost}
                aria-invalid={epostOk ? undefined : true}
                aria-describedby={epostOk ? undefined : `${id}-epost-fel`}
                onChange={(e) => {
                  setKvitto(false);
                  setEpost(e.target.value);
                }}
              />
              {!epostOk && (
                <div className="faltfel" id={`${id}-epost-fel`}>
                  Skriv en e-postadress som karin@byran.se.
                </div>
              )}
            </div>
          </div>
        )}
        {spara.isError && (
          <div className="meddelande" role="alert">
            {spara.error instanceof ApiFel && spara.error.status === 400
              ? 'Något fält är inte giltigt.'
              : 'Det gick inte att spara. Försök igen om en stund.'}
          </div>
        )}
        <div className="levande" aria-live="polite">
          {kvitto && <div className="kvitto">Inställningarna är sparade.</div>}
        </div>
        {kanAndra && (
          <div className="panel-fot slut">
            <button
              type="submit"
              className="btn btn-p"
              disabled={spara.isPending || !epostOk || !namnOk}
            >
              {spara.isPending ? 'Sparar …' : 'Spara'}
            </button>
          </div>
        )}
      </fieldset>
    </form>
  );
}
